import BaseBlockContent from '@sanity/block-content-to-react'
import React from 'react'
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Button,
  Typography
} from '@material-ui/core'
import { Link as InternalLink } from 'gatsby'
import ExpandMoreIcon from '@material-ui/icons/ExpandMore'
import BlockContent from './block-content'
import getReferenceUrl from '../../../utils/getReferenceUrl'
import theme from '../styles/theme'
import { makeStyles } from '@material-ui/core/styles'
import { convertLineBreakToBr } from '../lib/helpers'

import clsx from 'clsx'

const useStyles = makeStyles(theme => ({
  fullWidthImage: {
    breakInside: 'avoid',
    '& img': {
      width: '100%'
    }
  },
  twoCol: {
    display: 'flex',
    gap: '40px',
    '&>.col': {
      flexBasis: '100%',
      flex: 1,
      '& img': {
        width: '100%'
      }
    },
    [theme.breakpoints.down('xs')]: {
      display: 'block'
    }
  }
}))

const Content = props => {
  const { className, blocks } = props
  const classes = useStyles()

  const serializers = {
    types: {
      activityBlockContentObject(props) {
        return (
          <BlockContent
            className="activityBlockContentObject"
            blocks={props.node.content || props.node._rawContent}
          />
        )
      },
      blockContentObject(props) {
        return (
          <BlockContent
            className="blockContentObject"
            blocks={props.node.content || props.node._rawContent}
          />
        )
      },
      pageBreak(props) {
        return <div className="pageBreak">&nbsp;</div>
      },
      fullWidthImage(props) {
        const { asset } = props.node
        return (
          <div className={clsx('fullWidthImage', classes.fullWidthImage)}>
            {asset && <img src={asset.url} />}
          </div>
        )
      },
      imageWithCaption(props) {
        const { caption, asset } = props.node
        return (
          <div className={clsx('twoCol', classes.twoCol)}>
            <div className="col">{asset && <img src={asset.url} />}</div>
            <div className="col">
              <Typography
                variant="subtitle1"
                className="subtitle"
                style={{ color: theme.custom.colors.textBlue }}
              >
                {convertLineBreakToBr(caption)}
              </Typography>
            </div>
          </div>
        )
      },
      twoColumnText(props) {
        const { _rawCol1: col1, _rawCol2: col2 } = props.node
        return (
          <div className={clsx('twoCol', classes.twoCol)}>
            <div className="col">
              <BlockContent blocks={col1} />
            </div>
            <div className="col">
              <BlockContent blocks={col2} />
            </div>
          </div>
        )
      },
      accordion(props) {
        return (
          <>
            {props.node.items.map(item => (
              <Accordion key={item._key}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="h3">{item.title}</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <BlockContent blocks={item.content} />
                </AccordionDetails>
              </Accordion>
            ))}
          </>
        )
      },
      button(props) {
        const { buttonType, label } = props.node
        const { condition, linkMailTo, contactLink, linkExternal, linkInternal } = buttonType
        let mailToUrl
        switch (condition) {
          case 'linkMailTo':
            mailToUrl = `mailto:${linkMailTo.email}`
            if (linkMailTo.subject) {
              mailToUrl += `?subject=${linkMailTo.subject}`
            }
            return (
              <Button href={mailToUrl} target="_blank" rel="noopener">
                {label}
              </Button>
            )
          case 'contactLink':
            mailToUrl = `mailto:${contactLink.email}`
            if (contactLink.subject) {
              mailToUrl += `?subject=${contactLink.subject}`
            }
            return (
              <Button href={mailToUrl} target="_blank" rel="noopener">
                {label}
              </Button>
            )
          case 'linkExternal':
            return (
              <Button href={linkExternal} target="_blank" rel="noopener">
                {label}
              </Button>
            )
          case 'linkInternal':
            const url = getReferenceUrl(linkInternal)
            if (reference._type === 'activity') {
              return (
                <Button href={url} target="_blank">
                  {label}
                </Button>
              )
            } else {
              return (
                <Button component={InternalLink} to={url}>
                  {label}
                </Button>
              )
            }

          default:
            return <Button disabled>{label}</Button>
        }
      }
    }
  }

  return <BaseBlockContent className={className} blocks={blocks} serializers={serializers} />
}

export default Content
